/**
 * Creator Archive Page — /digest/@:username
 * Lists a creator's public digests, newest first, with subscribe form.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Volume2, Eye, Share2, Sparkles, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DigestArchivePage = ({ routeUsername } = {}) => {
  const { username: paramUsername } = useParams();
  const username = routeUsername || paramUsername;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/digest/by/${username}`);
        if (!cancelled) {
          setData(data);
          const display = (data.user_name || `@${username}`).split(' ')[0];
          document.title = `${display}'s Weekly Intelligence Digests — iSupplyAI Pulse`;
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'This creator has no public digests yet.' : 'Could not load archive.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  const submitSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/api/public/digest/by/${username}/subscribe`, { email });
      setSubscribed(true);
    } catch {
      setError('Subscribe failed — please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] text-gray-400 flex items-center justify-center" data-testid="digest-archive-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#020617] text-white flex flex-col items-center justify-center text-center px-4" data-testid="digest-archive-error">
        <div className="text-5xl mb-3">🔍</div>
        <h1 className="text-xl font-bold mb-2">@{username}</h1>
        <p className="text-gray-400 max-w-md mb-5">{error}</p>
        <Link to="/digest/featured" className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm">
          Browse the directory →
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="digest-archive-page">
      {/* Brand bar */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-bold text-sm">
            <span>🧠</span>
            <span>iSupplyAI <span className="text-purple-400 font-normal">Pulse</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/digest/featured" className="text-xs text-gray-400 hover:text-white">Browse all →</Link>
            <Link to="/?auth=login" className="text-xs px-3 py-1.5 rounded-lg text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500" data-testid="archive-login-btn">
              Login
            </Link>
            <Link to="/" className="text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold">
              Sign up →
            </Link>
          </div>
        </div>
      </div>

      {/* Creator hero */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-blue-900/30 p-6 sm:p-8">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-2xl font-bold">
              {(data.user_name || username).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-black text-white" data-testid="digest-archive-h1">
                  {(data.user_name || `@${username}`).split(' ')[0]}'s Weekly Intelligence Digests
                </h1>
                {(data.creator_tier || '').toLowerCase() === 'enterprise' && (
                  <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-300 text-[10px] font-bold uppercase tracking-widest border border-amber-500/40" data-testid="archive-enterprise-badge">
                    ⭐ Enterprise
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-400 flex flex-wrap gap-3 mt-1">
                <span>@{data.username}</span>
                <span>·</span>
                <span className="uppercase tracking-widest">{data.industry}</span>
                <span>·</span>
                <span>{data.subscribers} subscribers</span>
                <span>·</span>
                <span>{data.items.length} digests</span>
                {typeof data.views_this_week === 'number' && (
                  <>
                    <span>·</span>
                    <span className="text-cyan-400">👁 {data.views_this_week} views this week</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Subscribe */}
          {!subscribed ? (
            <form onSubmit={submitSubscribe} className="flex flex-col sm:flex-row gap-2 mt-4" data-testid="digest-archive-subscribe-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={`you@example.com — get notified when @${username} publishes`}
                required
                className="flex-1 bg-black/30 border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500"
                data-testid="digest-archive-subscribe-email"
              />
              <button
                type="submit"
                disabled={subscribing || !email}
                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm disabled:opacity-50"
                data-testid="digest-archive-subscribe-btn"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Subscribe'}
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 text-emerald-300 text-sm mt-4" data-testid="digest-archive-subscribed">
              <CheckCircle2 className="w-4 h-4" />
              Subscribed — you'll get an email when they publish.
            </div>
          )}
        </div>

        {/* Digest list */}
        <div className="mt-6 space-y-3">
          {data.items.map((d, i) => {
            const href = `/digest/@${d.username}/${d.week_slug}`;
            return (
              <Link
                to={href}
                key={`${d.username}-${d.week_slug}`}
                className="block rounded-2xl border border-gray-800 bg-gray-900/50 hover:border-purple-500/40 p-5 transition-all"
                data-testid={`digest-archive-item-${i}`}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{d.week_slug.replace(/-/g, ' ')}</div>
                    <div className="text-lg font-bold text-white">{(data.user_name || 'Weekly').split(' ')[0]}'s Week Ahead</div>
                    {(d.topics_preview || []).length > 0 && (
                      <ul className="mt-2 text-sm text-gray-400 space-y-0.5">
                        {d.topics_preview.slice(0, 2).map((t, j) => (
                          <li key={j} className="flex gap-2"><span className="text-purple-400">#{j + 1}</span><span className="truncate">{t}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 text-xs text-gray-500 flex-shrink-0">
                    {d.audio_included && <span className="flex items-center gap-1 text-amber-400/80"><Volume2 className="w-3 h-3" /> 60s</span>}
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {d.view_count || 0}</span>
                    <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {d.share_count || 0}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 p-6 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 to-blue-900/30">
          <div className="flex items-center justify-center gap-2 text-purple-300 text-xs font-bold uppercase tracking-widest mb-2">
            <Sparkles className="w-3 h-3" /> Powered by iSupplyAI
          </div>
          <h2 className="text-xl font-black mb-2">Get your own weekly digest</h2>
          <Link to="/" className="inline-block px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-sm">
            Start free →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DigestArchivePage;
